import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

import { isValidationStatus, readApiErrorMessages } from "@app/core/http/api-error.util";
import { HANDLE_VALIDATION_INLINE } from "@app/core/http/validation-inline.context";
import { ToastService } from "@app/core/services/toast.service";

const ErrServerCodes = [
  HttpStatusCode.InternalServerError,
  HttpStatusCode.NotImplemented,
  HttpStatusCode.BadGateway,
  HttpStatusCode.ServiceUnavailable,
  HttpStatusCode.GatewayTimeout,
  HttpStatusCode.HttpVersionNotSupported,
  HttpStatusCode.VariantAlsoNegotiates,
  HttpStatusCode.InsufficientStorage,
  HttpStatusCode.LoopDetected,
  HttpStatusCode.NotExtended,
  HttpStatusCode.NetworkAuthenticationRequired
];

const ErrorServerMsg = "Something went wrong.";
const REFRESH_URL_FRAGMENT = "/user/refresh";

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly toastService = inject(ToastService);

  intercept<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // A failed silent token refresh means the session expired / was revoked — that is handled by
        // UnauthorizedInterceptor (redirect to login + a friendly notice). Never surface the raw
        // backend "refresh token not found" message as a scary error toast.
        if (req.url.includes(REFRESH_URL_FRAGMENT)) {
          return throwError(() => error);
        }

        if (ErrServerCodes.includes(error.status)) {
          this.toastService.showError(ErrorServerMsg);

          return throwError(() => error);
        }

        // The form that sent the request shows validation errors next to its fields.
        if (req.context.get(HANDLE_VALIDATION_INLINE) && isValidationStatus(error.status)) {
          return throwError(() => error);
        }

        if (!error?.error) {
          this.toastService.showError(error?.statusText);
        } else {
          readApiErrorMessages(error).forEach(message => this.toastService.showError(message));
        }

        return throwError(() => error);
      })
    );
  }
}
